import { clsx } from "clsx";
import { Component } from "react";
import {
  type DndComponentClass,
  DragSource,
  type DragSourceMonitor,
  type DragSourceSpec,
  DropTarget,
  type DropTargetSpec,
} from "react-dnd";

import { HStack, SymbolIcon, TriggerButton, TriggerButtonSize } from "widgets";

import { Checkbox } from "../forms/Checkbox.js";
import { type IColumnNode } from "./ColumnSelectionDialog.js";

export interface ITableColumnItemProps {
  node: IColumnNode;
  classes?: any;
  onCheckboxClick: (event, id: number) => void;
  onToggleGroup: (id: number) => void;
  onMoveColumn?: (dragIndex: number, hoverIndex: number) => void;
  drop?: (props, monitor, component) => any;
  canDrag?: (propsOfDragged: ITableColumnItemProps) => boolean;
  canDrop?: (props: ITableColumnItemProps, monitor) => boolean;
  isDragging?: (props, monitor) => boolean;
  innerItem?: boolean;
  connectDropTarget?: any;
  connectDragSource?: any;
  isOver?: boolean;
  isOverClasses?: any;
  canDropValue?: boolean;
  canDragValue?: boolean;
  dragItem?: any;
}

/**
 * A column with sub-columns or not.
 * It can be dragged and dropped to some other positions.
 *
 * created on 3/19/2018
 */
class TableColumnItem extends Component<ITableColumnItemProps> {
  constructor(props: ITableColumnItemProps) {
    super(props);
  }

  createDragImageIcon = () => {
    const style = { height: 20 };

    return this.props.canDragValue ? (
      <SymbolIcon icon={"drag_indicator"} className="sortable-item" style={style} />
    ) : (
      <SymbolIcon
        icon={"drag_indicator"}
        className="sortable-item"
        style={{ ...style, cursor: "not-allowed", opacity: 0.3 }}
      />
    );
  };

  createLabelElement = (node: IColumnNode) => {
    const labelText = node.column ? node.column.name : node.groupName;

    return (
      <HStack>
        {labelText}
        {node.children.length > 0 && (
          <TriggerButton
            icon={<SymbolIcon icon={node.isExpanded ? "indeterminate_check_box" : "add_box"} size={16} filled />}
            label={`${node.isExpanded ? "Collapse" : "Expand"} column group`}
            onPress={() => this.props.onToggleGroup(node.id)}
            size={TriggerButtonSize.Small}
          />
        )}
      </HStack>
    );
  };

  render() {
    const { connectDragSource, connectDropTarget, node, isOver, canDropValue, dragItem } = this.props;
    const isOwnChild = dragItem?.node
      ? node.children.findIndex((c) => c.id === dragItem.node.id) > -1 && isOver
      : false;
    const canInsertBelow = isOver && canDropValue && !isOwnChild;

    const key = `${node.id}-${node.groupName ? node.groupName : ""}-${node.column ? node.column.name : ""}`;
    const disabled = node.column ? node.column.fixed : isFixedGroup(node);

    const checkedCount = node.children.reduce((count, n) => count + (n.column.hidden ? 0 : 1), 0);
    const checked = node.isGroup ? checkedCount === node.children.length : !node.column.hidden;
    const partiallySelected = node.isGroup ? checkedCount > 0 && checkedCount < node.children.length : false;

    const hoverClassNames = clsx({ "column-hover-insert-pos-2": canInsertBelow && !node.isGroup }, this.props.classes);
    const outerClassNames = clsx(
      "form-group",
      { "column-hover-insert-pos-2": node.isGroup && canInsertBelow },
      this.props.classes,
    );
    return connectDragSource(
      <div
        key={key}
        className={outerClassNames}
        style={{ position: "relative", marginBottom: 0, paddingBottom: canInsertBelow ? 2 : 10 }}
      >
        {connectDropTarget(
          <div className={hoverClassNames}>
            {this.createDragImageIcon()}
            <div className="checkbox check-default" style={{ marginTop: 0, marginBottom: 0, marginLeft: 15 }}>
              <Checkbox
                id={`column-check-${key}`}
                label={this.createLabelElement(node)}
                disabled={disabled}
                checked={checked}
                partiallyChecked={partiallySelected}
                onChange={(event) => this.props.onCheckboxClick(event, node.id)}
              />
            </div>
          </div>,
        )}
        {node.children.length > 0 && node.isExpanded && (
          <div style={{ marginLeft: 20, marginTop: 10 }}>
            {node.children.map((x, i) =>
              x.isFilteredOut ? null : (
                <DragDropTableColumnItem
                  key={`${x.id}-node`}
                  node={x}
                  canDrop={this.props.canDrop}
                  connectDropTarget={connectDropTarget}
                  connectDragSource={connectDragSource}
                  drop={this.props.drop}
                  onCheckboxClick={this.props.onCheckboxClick}
                  onToggleGroup={this.props.onToggleGroup}
                  classes={i === 0 && isOwnChild && canDropValue ? "column-hover-first-child-insert-pos-2" : ""}
                />
              ),
            )}
          </div>
        )}
      </div>,
    );
  }
}

/*
  If child item in the group is fixed, consider the group as fixed
 */
export function isFixedGroup(node: IColumnNode) {
  if (node.isGroup) {
    let fixed = false;
    for (let i = 0; i < node.children.length && !fixed; i++) {
      const c = node.children[i];
      if (c.isGroup) {
        fixed = isFixedGroup(c);
      } else {
        fixed = c.column.fixed;
        if (fixed) {
          return true;
        }
      }
    }
    return fixed;
  } else {
    return false;
  }
}

const columnSourceSpec: DragSourceSpec<ITableColumnItemProps, ITableColumnItemProps> = {
  beginDrag(
    propsOfDragged: ITableColumnItemProps,
    _monitor: DragSourceMonitor,
    _component?: Component<ITableColumnItemProps, any>,
  ): ITableColumnItemProps {
    return { ...propsOfDragged };
  },
  canDrag(propsOfDragged: ITableColumnItemProps): boolean {
    return propsOfDragged.node.column ? !propsOfDragged.node.column.fixed : !isFixedGroup(propsOfDragged.node);
  },
  isDragging(propsOfDragged: ITableColumnItemProps, monitor: DragSourceMonitor) {
    return propsOfDragged.node.id === (monitor.getItem() as ITableColumnItemProps).node.id;
  },
};

const columnTargetSpec: DropTargetSpec<ITableColumnItemProps> = {
  canDrop(props: ITableColumnItemProps, monitor): boolean {
    if (props.canDrop) {
      return props.canDrop(props, monitor);
    }
  },
  drop(props: ITableColumnItemProps, monitor, component): any {
    if (props.drop) {
      props.drop(props, monitor, component);
    }
  },
};

const GROUP_TABLE_COLUMN_NODE = "GROUP_TABLE_COLUMN_NODE";

export const DragDropTableColumnItem: DndComponentClass<any, ITableColumnItemProps> = DragSource(
  GROUP_TABLE_COLUMN_NODE,
  columnSourceSpec,
  (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    canDragValue: monitor.canDrag(),
    isDragging: monitor.isDragging(),
  }),
)(
  DropTarget(GROUP_TABLE_COLUMN_NODE, columnTargetSpec, (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver({ shallow: true }), // report for inner-most node only
    dragItem: monitor.getItem(),
    canDropValue: monitor.canDrop(),
    isDragging: () => false,
  }))(TableColumnItem),
);
